import React, {useEffect, useMemo, useState} from "react"
import PropTypes from "prop-types"
import {Box, Button, Chip, Link, Stack, Typography} from "@mui/material"

const ACTIVE = new Set(["running", "IN_PROGRESS", "PROCESSING", "CALCULATING", "SETUP_TASK", "FILE_PROCESSING", "GENERATING_MESSAGE", "READY_TO_CALCULATE", "REPORT_IN_PROGRESS", "NOTIFICATION_IN_PROGRESS"])

export const timestamp = value => {
    if (typeof value !== "string" || !/(Z|[+-]\d{2}:\d{2})$/i.test(value)) return null
    const result = Date.parse(value)
    return Number.isFinite(result) ? result : null
}

export const formatExecutionDate = value => {
    const time = timestamp(value)
    return time == null ? "—" : new Date(time).toLocaleString("pt-BR", {timeZone: "America/Sao_Paulo"})
}

export const formatDuration = seconds => {
    if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "—"
    const total = Math.floor(seconds)
    return [Math.floor(total / 3600), Math.floor(total / 60) % 60, total % 60].map(v => String(v).padStart(2, "0")).join(":")
}

export const executionTiming = (execution, now = Date.now()) => {
    const timing = execution.timing
    if (timing?.source === "airflow") {
        const start = timestamp(timing.started_at)
        const finish = timestamp(timing.finished_at)
        const active = timing.state === "running" && finish == null
        const end = finish ?? (active ? now : null)
        return {...timing, label: "Duração da execução", active,
            seconds: start != null && end != null && end >= start ? (end - start) / 1000 : null}
    }
    const stages = execution.stages || []
    const starts = stages.map(s => timestamp(s.started_at)).filter(t => t != null)
    const ends = stages.map(s => timestamp(s.finished_at)).filter(t => t != null)
    const start = starts.length ? Math.min(...starts) : null
    // This fallback describes observed stages, never the full run or a live estimate.
    const end = ends.length ? Math.max(...ends) : null
    return {label: "Tempo das etapas", active: false,
        started_at: start == null ? null : new Date(start).toISOString(),
        finished_at: end == null ? null : new Date(end).toISOString(),
        seconds: start != null && end != null && end >= start ? (end - start) / 1000 : null}
}

export const groupWorkflowStages = stages => {
    const groups = new Map()
    const rows = []
    // Registration IDs are immutable; database response order and update times are not.
    const registered = stages.map((stage, index) => ({stage, index})).sort((a, b) => {
        if (Number.isInteger(a.stage.id) && Number.isInteger(b.stage.id)) return a.stage.id - b.stage.id
        if (Number.isInteger(a.stage.id)) return -1
        if (Number.isInteger(b.stage.id)) return 1
        return a.index - b.index
    })
    for (const {stage, index} of registered) {
        if (!stage.task_id || !Number.isInteger(stage.map_index) || stage.map_index < 0) {
            rows.push({key: `stage:${stage.id ?? stage.execution_key ?? index}`, stage})
            continue
        }
        if (!groups.has(stage.task_id)) {
            const group = {key: `mapped:${stage.task_id}`, taskId: stage.task_id, stages: [], latest: new Map()}
            groups.set(stage.task_id, group)
            rows.push(group)
        }
        const group = groups.get(stage.task_id)
        group.stages.push(stage)
        const previous = group.latest.get(stage.map_index)
        if (!previous || Number(stage.attempt ?? 0) >= Number(previous.attempt ?? 0)) group.latest.set(stage.map_index, stage)
    }
    for (const group of groups.values()) {
        group.stages.sort((a, b) => a.map_index - b.map_index || Number(a.attempt ?? 0) - Number(b.attempt ?? 0))
        group.counts = [...group.latest.values()].reduce((counts, s) => {
            const status = s.status || "Não informado"
            counts[status] = (counts[status] || 0) + 1
            return counts
        }, {})
    }
    return rows
}

export const WorkflowExecutionTiming = ({execution, visible = true}) => {
    const [now, setNow] = useState(Date.now)
    const active = executionTiming(execution).active
    useEffect(() => {
        if (!visible || !active) return undefined
        const timer = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(timer)
    }, [visible, active, execution.run_id])
    const timing = executionTiming(execution, now)
    return <Stack spacing={0.5}>
        <Typography variant="body2">Solicitação recebida pelo Airflow: {formatExecutionDate(timing.received_at)}</Typography>
        <Typography variant="body2">Registro na plataforma: {formatExecutionDate(execution.created_at)}</Typography>
        <Typography variant="body2">{timing.source === "airflow" ? "Início" : "Primeira etapa"}: {formatExecutionDate(timing.started_at)}</Typography>
        <Typography variant="body2">{timing.source === "airflow" ? "Término" : "Última etapa concluída"}: {formatExecutionDate(timing.finished_at)}</Typography>
        <Typography variant="body2" fontWeight={700}>{timing.label}: {formatDuration(timing.seconds)}</Typography>
        <Typography variant="caption">Horário de Brasília{timing.source !== "airflow" ? " · Horários completos do Airflow indisponíveis" : ""}</Typography>
    </Stack>
}

const StageDetails = ({stage, statusPresentation}) => {
    const status = statusPresentation(stage.status)
    const start = timestamp(stage.started_at)
    const finish = timestamp(stage.finished_at)
    const duration = start != null && finish != null ? (finish - start) / 1000 :
        (ACTIVE.has(stage.status) ? stage.duration_seconds : null)
    return <Box sx={{border: "1px solid", borderColor: "divider", p: 1.5, borderRadius: 1}}>
        <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" fontWeight={700} sx={{wordBreak: "break-word"}}>{stage.task_id || stage.execution_key || "Etapa"}</Typography>
            <Chip size="small" label={status.text}/>
        </Stack>
        <Typography variant="caption" display="block">Tentativa {stage.attempt == null ? "—" : Number(stage.attempt) + 1}{stage.map_index >= 0 && stage.map_index != null ? ` · Item ${stage.map_index}` : ""} · {formatDuration(duration)}</Typography>
        <Typography variant="caption" display="block">{formatExecutionDate(stage.started_at)} → {formatExecutionDate(stage.finished_at)}</Typography>
        {(stage.source || stage.curve) && <Typography variant="caption" display="block">Origem: {stage.source || "—"} · Curva: {stage.curve || "—"}</Typography>}
        {stage.work_item && <Typography variant="caption" display="block">Item de trabalho: {typeof stage.work_item === "string" ? stage.work_item : JSON.stringify(stage.work_item)}</Typography>}
        {[stage.artifact_summary, stage.error].filter(v => v && (typeof v !== "object" || Object.keys(v).length)).map((value, i) =>
            <Box component="pre" key={i} sx={{fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word"}}>{JSON.stringify(value, null, 2)}</Box>)}
        {stage.airflow_url && <Link href={stage.airflow_url} target="_blank" rel="noreferrer" variant="caption">Abrir tarefa no Airflow</Link>}
    </Box>
}

export const WorkflowExecutionStages = ({stages = [], executionKey, statusPresentation}) => {
    const [expanded, setExpanded] = useState({})
    useEffect(() => setExpanded({}), [executionKey])
    const rows = useMemo(() => groupWorkflowStages(stages), [stages])
    return <Stack spacing={1.5}>{rows.map(row => row.stage ?
        <StageDetails key={row.key} stage={row.stage} statusPresentation={statusPresentation}/> :
        <Box key={row.key} sx={{border: "1px solid", borderColor: "divider", p: 1.5, borderRadius: 1}}>
            <Button aria-expanded={Boolean(expanded[row.key])} onClick={() => setExpanded(value => ({...value, [row.key]: !value[row.key]}))} sx={{textTransform: "none", wordBreak: "break-word", textAlign: "left"}}>
                {expanded[row.key] ? "Recolher" : "Expandir"} {row.taskId} · {row.latest.size} itens registrados
            </Button>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">{Object.entries(row.counts).map(([status, count]) => <Chip key={status} size="small" label={`${statusPresentation(status).text}: ${count}`}/>)}</Stack>
            {expanded[row.key] && <Stack spacing={1} sx={{mt: 1}}>{row.stages.map((stage, i) => <StageDetails key={`${stage.map_index}:${stage.attempt ?? 0}:${stage.id ?? i}`} stage={stage} statusPresentation={statusPresentation}/>)}</Stack>}
        </Box>
    )}</Stack>
}

WorkflowExecutionTiming.propTypes = {
    execution: PropTypes.object.isRequired,
    visible: PropTypes.bool,
}
StageDetails.propTypes = {
    stage: PropTypes.object.isRequired,
    statusPresentation: PropTypes.func.isRequired,
}
WorkflowExecutionStages.propTypes = {
    stages: PropTypes.array,
    executionKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    statusPresentation: PropTypes.func.isRequired,
}
